import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MobileLayout } from '@components/layout/MobileLayout';
import { Header } from '@components/layout/Header';
import { Button } from '@components/ui/Button/Button';
import { InfoCard } from '@components/common/InfoCard';
import { ROUTES } from '@utils/constants';
import { createStudent } from '@api/students';
import type { CreateStudentInput } from '@api/students';
import { useToastContext } from '../../context/ToastContext';
import './styles/setupservice.css';

interface TransportType {
  id: string;
  label: string;
  description: string;
  icon: string;
}

interface SetupServiceState {
  formData: any;
  campuses: any[];
  shifts: any[];
  pickupLocation: {
    coordinates: {
      latitude: number;
      longitude: number;
    };
    address: string;
  };
}

const TRANSPORT_TYPES: TransportType[] = [
  {
    id: 'ac',
    label: 'AC Transport',
    description: 'Climate-controlled comfort',
    icon: '❄️',
  },
  {
    id: 'non-ac',
    label: 'Non-AC Transport',
    description: 'Well-ventilated & economical',
    icon: '🌬️',
  },
];

export const SetupService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SetupServiceState;
  const { addToast } = useToastContext();

  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().split('T')[0],
  );
  const [transportType, setTransportType] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!state?.formData || !state?.pickupLocation) {
      navigate(ROUTES.STUDENT_DETAILS);
      return;
    }
  }, []);

  const handleContinue = async () => {
    if (!startDate || !transportType) {
      addToast({
        type: 'error',
        title: 'Missing Information',
        message: 'Please select both start date and transport type',
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare student data
      const studentData: CreateStudentInput = {
        givenName: state.formData.givenName.trim(),
        familyName: state.formData.familyName?.trim(),
        gender: state.formData.gender,
        birthdate: state.formData.birthdate
          ? new Date(state.formData.birthdate).toISOString().split('T')[0]
          : undefined,
        campusId: state.formData.campusId?.id || '',
        location: {
          lat: state.pickupLocation.coordinates.latitude,
          lng: state.pickupLocation.coordinates.longitude,
        },
        geoAddress: state.pickupLocation.address,
        grade: state.formData.grade?.value,
        shifts: [state.formData.shiftId?.id || ''],
        startDate,
        status: 'PENDING_TOKEN_AMOUNT',
      };

      // Create student
      const createdStudent = await createStudent(studentData);

      if (!createdStudent?.id) {
        throw new Error('Failed to create student');
      }

      // Navigate to review details page
      navigate(ROUTES.REVIEW_DETAILS, {
        state: {
          formData: state.formData,
          campuses: state.campuses,
          shifts: state.shifts,
          pickupLocation: state.pickupLocation,
          transportType,
          startDate,
          studentId: createdStudent.id,
        },
      });
    } catch (err: any) {
      console.error('Error creating student:', err);
      addToast({
        type: 'error',
        title: 'Failed to Create Student',
        message: err.message || 'Failed to create student. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = startDate && transportType;

  return (
    <MobileLayout
      header={<Header showBack />}
      footer={
        <Button
          fullWidth
          onClick={handleContinue}
          disabled={!isFormValid || loading}
          loading={loading}
        >
          Continue →
        </Button>
      }
      showBottomPadding
    >
      <div className="setup-service">
        <div className="setup-service__header">
          <h1 className="setup-service__title">Setup your service</h1>
          <p className="setup-service__subtitle">
            Configure pickup location, start date, and transport type
          </p>
        </div>

        <div className="setup-service__section">
          <div className="setup-service__section-header">
            <span className="setup-service__section-icon">📍</span>
            <div>
              <h3 className="setup-service__section-title">Pickup location</h3>
              <p className="setup-service__section-desc">
                AC doorstep pickup & drop
              </p>
            </div>
          </div>
          <div className="setup-service__location-display">
            <div className="setup-service__search-input">
              <svg
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search for your address..."
                value={state?.pickupLocation?.address || ''}
                readOnly
                className="setup-service__input"
              />
            </div>
            <button className="setup-service__location-btn">📍</button>
          </div>
          <div className="setup-service__map-preview">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 400 150"
              preserveAspectRatio="none"
            >
              <defs>
                <pattern
                  id="grid"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="1"
                  />
                </pattern>
              </defs>
              <rect width="400" height="150" fill="url(#grid)" />
              <circle cx="200" cy="75" r="6" fill="#FFD700" stroke="#FFA500" strokeWidth="2" />
            </svg>
          </div>
        </div>

        <div className="setup-service__section">
          <div className="setup-service__section-header">
            <span className="setup-service__section-icon">📅</span>
            <h3 className="setup-service__section-title">Subscription start date</h3>
            <p className="setup-service__section-desc">
              When should we begin your service?
            </p>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="setup-service__date-input"
          />
        </div>

        <div className="setup-service__section">
          <div className="setup-service__section-header">
            <span className="setup-service__section-icon">🚌</span>
            <h3 className="setup-service__section-title">Transport type</h3>
            <p className="setup-service__section-desc">
              Select your preferred option(s)
            </p>
          </div>
          <div className="setup-service__transport-options">
            {TRANSPORT_TYPES.map((type) => (
              <button
                key={type.id}
                className={`setup-service__transport-option ${
                  transportType === type.id ? 'active' : ''
                }`}
                onClick={() => setTransportType(type.id)}
              >
                <span className="setup-service__transport-icon">{type.icon}</span>
                <div className="setup-service__transport-content">
                  <h4 className="setup-service__transport-label">{type.label}</h4>
                  <p className="setup-service__transport-desc">{type.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <InfoCard
          title="DID YOU KNOW?"
          description="Omni's seating capacity is 5 including the driver. Anything more is illegal and unsafe."
          variant="info"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path
                d="M12 8V12L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          }
        />
      </div>
    </MobileLayout>
  );
};
